import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { getApiUrl as resolveApiUrl } from "@/lib/runtimeConfig";
import { formatApiErrorDetail } from "@/lib/api";
import { AUTH_INPUT_CLS, AuthError } from "@/components/auth/AuthShell";

const API = resolveApiUrl();
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

const STEPS = [
    { key: "request", label: "Request filed by a super admin of this environment" },
    { key: "email", label: "Confirmed from the super admin's mailbox (link valid 30 min)" },
    { key: "hub", label: "Approved by a hub super admin (within 24 h)" },
];
const STEP_DONE = { none: 0, pending_email: 1, awaiting_hub: 2, approved: 3, executing: 3 };

/** Sober reset-to-blank section shown on /init once setup is closed. */
export function InitResetSection() {
    const [params, setParams] = useSearchParams();
    const token = params.get("reset_token");
    const [state, setState] = useState(null);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => axios.get(`${API}/init/reset/status`).then((r) => setState(r.data)).catch(() => setState({ state: "unreachable" })), []);

    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => {
        if (!token) return;
        let alive = true;
        axios.post(`${API}/init/reset/confirm`, { token })
            .then((r) => { if (alive) { setState(r.data); setNotice("Confirmed. The request now awaits approval by a hub super admin."); } })
            .catch((e) => { if (alive) setError(e?.response?.status === 410 ? "This confirmation link is no longer valid (used or older than 30 minutes)." : formatApiErrorDetail(e?.response?.data?.detail)); });
        return () => { alive = false; };
    }, [token]);

    const request = async (e) => {
        e.preventDefault(); setBusy(true); setError(null); setNotice(null);
        try {
            const r = await axios.post(`${API}/init/reset/request`, { email: email.trim() });
            setNotice(r.data.message); setEmail("");
            setTimeout(load, 800);
        } catch (e2) {
            setError(formatApiErrorDetail(e2?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const cancel = async () => {
        if (!window.confirm("Cancel the active reset request?")) return;
        setBusy(true); setError(null);
        try {
            const r = await axios.post(`${API}/init/reset/cancel`, { token: token || null });
            setState(r.data); setNotice("Request cancelled.");
            if (token) setParams({});
        } catch (e2) {
            setError(e2?.response?.status === 403 ? "Only the requester (via the confirmation link) or a signed-in super admin can cancel." : formatApiErrorDetail(e2?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    if (!state || state.state === "unreachable") return null;
    if (state.state === "not_available") {
        return (
            <div className="mt-6 border-t border-white/10 pt-5 text-xs text-white/40" data-testid="init-reset-unavailable">
                {state.reason === "hub" ? "This deployment is the Radar Sync hub; it cannot be reset through this page." : "Reset to blank requires a hub link (RADAR_HUB_URL + RADAR_SYNC_KEY) — not configured on this deployment."}
            </div>
        );
    }
    const active = ["pending_email", "awaiting_hub", "approved", "executing"].includes(state.state);
    const done = STEP_DONE[state.state] ?? 0;
    return (
        <div className="mt-6 border-t border-white/10 pt-5" data-testid="init-reset-section">
            <h2 className="text-base font-display tracking-tight mb-1">Reset this deployment</h2>
            <p className="text-xs text-white/50 mb-4">
                Erases the <span className="text-white/80">entire database</span> of this environment — administrators, tenants and API keys, the Radar corpus, saved views, exclusions, settings and the audit log — and reopens this setup page. Stored files are kept. A linked environment re-downloads its Radar data from the hub afterwards. Three independent confirmations are required:
            </p>
            <ol className="space-y-1.5 mb-4" data-testid="init-reset-checklist">
                {STEPS.map((s, i) => (
                    <li key={s.key} className="flex items-start gap-2 text-xs" data-testid={`init-reset-step-${s.key}`}>
                        <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px] ${i < done ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-200" : "border-white/20 text-white/40"}`}>{i < done ? "✓" : i + 1}</span>
                        <span className={i < done ? "text-white/80" : "text-white/50"}>{s.label}</span>
                    </li>
                ))}
            </ol>
            {active && (
                <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 text-xs mb-3" data-testid="init-reset-status">
                    <div className="text-white/80">State: <span className="font-mono" data-testid="init-reset-state">{state.state.replace("_", " ")}</span> <span className="text-white/30">· {state.request_id}</span></div>
                    {state.state === "pending_email" && <div className="text-white/50 mt-1">Waiting for the confirmation link to be opened — expires {fmt(state.email_expires_at)}.</div>}
                    {state.state === "awaiting_hub" && <div className="text-white/50 mt-1">Waiting for a hub super admin ({state.hub}) — expires {fmt(state.hub_expires_at)}.</div>}
                    {state.state === "approved" && <div className="text-amber-200 mt-1">Approved{state.decision?.by ? ` by ${state.decision.by}` : ""} — the wipe starts within 30 seconds.</div>}
                    {state.state === "executing" && <div className="text-amber-200 mt-1">Erasing the database…</div>}
                    {state.state !== "executing" && (
                        <button type="button" onClick={cancel} disabled={busy} className="mt-2 text-[11px] text-white/50 hover:text-white underline disabled:opacity-40" data-testid="init-reset-cancel-btn">Cancel this request</button>
                    )}
                </div>
            )}
            {!active && state.last && (
                <div className="text-[11px] text-white/40 mb-3" data-testid="init-reset-last">Last request: {state.last.state}{state.last.decision?.by ? ` by ${state.last.decision.by}` : ""} · {fmt(state.last.updated_at)}</div>
            )}
            {!active && (
                <form onSubmit={request} className="flex gap-2" data-testid="init-reset-form">
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="super admin email of this environment" className={`${AUTH_INPUT_CLS} flex-1`} data-testid="init-reset-email-input" />
                    <button type="submit" disabled={busy} className="rounded-md border border-white/15 px-3 py-2 text-xs text-white/70 hover:text-white hover:border-white/30 disabled:opacity-40" data-testid="init-reset-request-btn">
                        {busy ? "…" : "Request reset"}
                    </button>
                </form>
            )}
            {notice && <div className="mt-3 text-xs text-white/70 border border-white/10 rounded-md px-3 py-2" data-testid="init-reset-notice">{notice}</div>}
            <div className="mt-3"><AuthError message={error} testId="init-reset-error" /></div>
        </div>
    );
}
